/*
 * necklace-glow.js
 * ---------------------------------------------------------------------------
 * Transparent 2D-canvas overlay for a collection card image. Pulses a soft glow
 * at each configured point (the necklaces) one at a time, in a loop.
 *
 * The canvas uses CSS `mix-blend-mode: screen`, so transparent areas leave the
 * image untouched and the bright glow only adds light. Points are normalised
 * (0–1) to the image, so they track it at any size. Cheap: one 2D context, a few
 * radial gradients per frame, paused when off-screen.
 *
 * Tunables (data-* on the canvas):
 *   data-points "x,y x,y x,y"  normalised glow centres (default = 3 necklaces)
 *   data-color  "#fff6e0"      glow colour
 *   data-radius 0.10           glow radius as a fraction of canvas width
 *   data-cycle  4.5            seconds for one full loop through all points
 */

const PREFERS_REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function parsePoints(str) {
  if (!str) return null;
  const pts = str
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(",").map(Number);
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
    })
    .filter(Boolean);
  return pts.length ? pts : null;
}

class NecklaceGlow {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.points = parsePoints(canvas.dataset.points) || [
      { x: 0.2, y: 0.44 },
      { x: 0.49, y: 0.66 },
      { x: 0.8, y: 0.9 },
    ];
    this.color = canvas.dataset.color || "#fff6e0";
    this.radiusFrac = parseFloat(canvas.dataset.radius) || 0.13;
    this.cycle = parseFloat(canvas.dataset.cycle) || 4.5;

    this.host = canvas.parentElement || canvas;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.start = performance.now();
    this.running = false;
    this.visible = true;
    this.disposed = false;

    this._rgb = this._hexToRgb(this.color);

    this._resize();
    this._observe();
    if (PREFERS_REDUCED_MOTION) this.drawFrame(this.start);
    else this._start();
  }

  _hexToRgb(hex) {
    const h = hex.replace("#", "");
    const n = parseInt(
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h,
      16,
    );
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  _resize() {
    const rect = this.host.getBoundingClientRect();
    const w = rect.width || this.host.clientWidth;
    const h = rect.height || this.host.clientHeight;
    if (!w || !h) {
      // Not laid out yet (e.g. ratio box height not resolved) — retry next frame.
      requestAnimationFrame(() => this._resize());
      return;
    }
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
  }

  _observe() {
    this._io = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this._start();
        else this._stop();
      },
      { threshold: 0.01 },
    );
    this._io.observe(this.host); // observe the sized parent, not the (maybe 0-size) canvas

    this._ro = new ResizeObserver(() => this._resize());
    this._ro.observe(this.host);

    this._onVisibility = () => {
      if (document.hidden) this._stop();
      else if (this.visible) this._start();
    };
    document.addEventListener("visibilitychange", this._onVisibility);
  }

  _start() {
    if (this.running || this.disposed || PREFERS_REDUCED_MOTION) return;
    this.running = true;
    this._loop = (now) => {
      if (!this.running) return;
      this.drawFrame(now);
      this._raf = requestAnimationFrame(this._loop);
    };
    this._raf = requestAnimationFrame(this._loop);
  }

  _stop() {
    this.running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  // Intensity for point i at normalised cycle time u (0–1): a smooth raised
  // cosine pulse centred on that point's slot, with slight overlap so glows
  // hand off gently instead of snapping.
  _intensity(i, u) {
    const n = this.points.length;
    const center = (i + 0.5) / n;
    let d = Math.abs(u - center);
    d = Math.min(d, 1 - d); // wrap around the loop
    const half = 0.6 / n; // > 0.5/n → adjacent pulses overlap a little
    const k = d / half;
    return k >= 1 ? 0 : 0.5 * (1 + Math.cos(Math.PI * k));
  }

  drawFrame(now) {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);

    const u = (((now - this.start) / 1000 / this.cycle) % 1 + 1) % 1;
    const radius = this.radiusFrac * w;
    const { r, g, b } = this._rgb;

    ctx.globalCompositeOperation = "lighter"; // additive, so overlaps don't dim
    for (let i = 0; i < this.points.length; i++) {
      const intensity = this._intensity(i, u);
      if (intensity <= 0.001) continue;
      const p = this.points[i];
      const cx = p.x * w;
      const cy = p.y * h;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, `rgba(255,255,255,${0.95 * intensity})`); // hot white core
      grad.addColorStop(0.25, `rgba(${r},${g},${b},${0.7 * intensity})`);
      grad.addColorStop(0.6, `rgba(${r},${g},${b},${0.25 * intensity})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = "source-over";
  }

  dispose() {
    this.disposed = true;
    this._stop();
    if (this._onVisibility)
      document.removeEventListener("visibilitychange", this._onVisibility);
    if (this._io) this._io.disconnect();
    if (this._ro) this._ro.disconnect();
  }
}

function init() {
  document.querySelectorAll("[data-necklace-glow]").forEach((el) => {
    if (el._necklaceGlow) return;
    el._necklaceGlow = new NecklaceGlow(el);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
